import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { services } from '../data/mock';
import * as Icons from 'lucide-react';
import { Button } from './ui/button';

const ServicesSection = ({ onOpenPopup }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [currentX, setCurrentX] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % allItems.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const handleDragStart = (e) => {
    setIsDragging(true);
    setStartX(e.type === 'mousedown' ? e.clientX : e.touches[0].clientX);
    setCurrentX(e.type === 'mousedown' ? e.clientX : e.touches[0].clientX);
  };

  const handleDragMove = (e) => {
    if (!isDragging) return;
    const x = e.type === 'mousemove' ? e.clientX : e.touches[0].clientX;
    setCurrentX(x);
  };

  const handleDragEnd = () => {
    if (!isDragging) return;
    const diff = startX - currentX;
    const threshold = 50;

    if (diff > threshold) {
      setCurrentIndex((prev) => (prev + 1) % allItems.length);
    } else if (diff < -threshold) {
      setCurrentIndex((prev) => (prev - 1 + allItems.length) % allItems.length);
    }

    setIsDragging(false);
  };

  // Create array with 5 services + 1 CTA box
  const allItems = [
    ...services,
    {
      id: 'cta',
      title: 'Ready to Get Started?',
      description: 'Protect your brand today with our expert trademark registration services. Let us help you secure your intellectual property.',
      icon: 'Sparkles',
      isCTA: true
    }
  ];

  // Get 3 services to display at current position for desktop
  const getVisibleServices = () => {
    const visible = [];
    for (let i = 0; i < 3; i++) {
      visible.push(allItems[(currentIndex + i) % allItems.length]);
    }
    return visible;
  };

  return (
    <section className="pt-20 pb-4 md:pb-20 bg-gradient-to-b from-white to-purple-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Our Services
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Comprehensive intellectual property protection services tailored to your needs
          </p>
        </div>

        {/* Desktop View - Show 3 services with sliding */}
        <div className="hidden md:block">
          <div className="grid grid-cols-3 gap-8">
            {getVisibleServices().map((item, index) => {
              const Icon = Icons[item.icon];
              
              if (item.isCTA) {
                return (
                  <div
                    key={`${item.id}-${currentIndex}-${index}`}
                    className="group bg-gradient-to-br from-purple-600 to-purple-800 rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 flex flex-col justify-between animate-fadeIn"
                  >
                    <div>
                      <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 border border-white/30">
                        <Icon className="h-8 w-8 text-white" />
                      </div>
                      <h3 className="text-2xl font-bold text-white mb-3">
                        {item.title}
                      </h3>
                      <p className="text-purple-100 leading-relaxed mb-6">
                        {item.description}
                      </p>
                    </div>
                    <Button
                      onClick={onOpenPopup}
                      className="w-full bg-white text-purple-800 hover:bg-purple-50 py-6 text-lg font-semibold rounded-xl shadow-lg transition-all duration-300"
                    >
                      Get Started Now
                    </Button>
                  </div>
                );
              }

              return (
                <Link
                  key={`${item.id}-${currentIndex}-${index}`}
                  to={`/services/${item.slug}`}
                  className="group bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 animate-fadeIn"
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-purple-800 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <Icon className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-purple-700 transition-colors">
                    {item.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed mb-4">
                    {item.description}
                  </p>
                  <div className="flex items-center text-purple-700 font-semibold group-hover:text-purple-800">
                    Learn More
                    <Icons.ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-2 transition-transform" />
                  </div>
                </Link>
              );
            })}
          </div>

          {/* Navigation Dots */}
          <div className="flex justify-center mt-8 space-x-2">
            {allItems.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                  currentIndex === index ? 'bg-purple-700 w-8' : 'bg-gray-300'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Mobile Slider */}
        <div className="md:hidden relative">
          <div 
            className="overflow-hidden cursor-grab active:cursor-grabbing"
            onMouseDown={handleDragStart}
            onMouseMove={handleDragMove}
            onMouseUp={handleDragEnd}
            onMouseLeave={handleDragEnd}
            onTouchStart={handleDragStart}
            onTouchMove={handleDragMove}
            onTouchEnd={handleDragEnd}
          >
            <div
              className="flex transition-transform duration-500 ease-in-out"
              style={{ transform: `translateX(-${currentIndex * 100}%)` }}
            >
              {allItems.map((item) => {
                const Icon = Icons[item.icon];
                
                if (item.isCTA) {
                  return (
                    <div key={item.id} className="w-full flex-shrink-0 px-4">
                      <div className="bg-gradient-to-br from-purple-600 to-purple-800 rounded-2xl p-8 shadow-lg flex flex-col justify-between">
                        <div>
                          <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center mb-6 border border-white/30">
                            <Icon className="h-8 w-8 text-white" />
                          </div>
                          <h3 className="text-2xl font-bold text-white mb-3">
                            {item.title}
                          </h3>
                          <p className="text-purple-100 leading-relaxed mb-6">
                            {item.description}
                          </p>
                        </div>
                        <Button
                          onClick={onOpenPopup}
                          className="w-full bg-white text-purple-800 hover:bg-purple-50 py-6 text-lg font-semibold rounded-xl shadow-lg"
                        >
                          Get Started Now
                        </Button>
                      </div>
                    </div>
                  );
                }

                return (
                  <Link key={item.id} to={`/services/${item.slug}`} className="w-full flex-shrink-0 px-4">
                    <div className="bg-white rounded-2xl p-8 shadow-lg">
                      <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-purple-800 rounded-xl flex items-center justify-center mb-6">
                        <Icon className="h-8 w-8 text-white" />
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-3">
                        {item.title}
                      </h3>
                      <p className="text-gray-600 leading-relaxed mb-4">
                        {item.description}
                      </p>
                      <div className="flex items-center text-purple-700 font-semibold">
                        Learn More
                        <Icons.ArrowRight className="ml-2 h-5 w-5" />
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>

          {/* Navigation Dots */}
          <div className="flex justify-center mt-6 space-x-2">
            {allItems.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                  currentIndex === index ? 'bg-purple-700 w-8' : 'bg-gray-300'
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
